// ============================================================================
// Vista — Cupones (panel-cliente).
// ============================================================================

import { crearTabla, mostrarToast, iconoCandado } from '@plataforma/ui';
import { escapeAttr, mensajeErrorAmigable, tieneFeature } from '@plataforma/utils';

import {
  activarCupon,
  actualizarCupon,
  crearCupon,
  desactivarCupon,
  eliminarCupon,
  listarCupones,
} from '../api/cupones.js';
import { obtenerEstadoSesion } from '../estado-sesion.js';
import { exigirSesionEmpresa } from '../guardas.js';

import { montarShell } from './shell.js';

function formatearFecha(valor) {
  if (!valor) return '—';
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) return '—';
  return fecha.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' });
}

function derivarEstado(cupon) {
  if (cupon.estado === 'inactivo') return { etiqueta: 'Inactivo', tono: 'neutral' };
  const ahora = new Date();
  if (cupon.fecha_fin && new Date(cupon.fecha_fin) < ahora)
    return { etiqueta: 'Vencido', tono: 'peligro' };
  if (cupon.fecha_inicio && new Date(cupon.fecha_inicio) > ahora)
    return { etiqueta: 'Programado', tono: 'info' };
  if (cupon.usos_maximos !== null && cupon.usos_actuales >= cupon.usos_maximos)
    return { etiqueta: 'Agotado', tono: 'peligro' };
  return { etiqueta: 'Activo', tono: 'exito' };
}

function renderizarBadge(estado) {
  const badge = document.createElement('pf-badge-estado');
  badge.setAttribute('tono', estado.tono);
  badge.textContent = estado.etiqueta;
  return badge;
}

function textoAplicacion(cupon) {
  if (cupon.aplicacion === 'todos') return 'Toda la tienda';
  if (cupon.aplicacion === 'productos') return 'Productos específicos';
  if (cupon.aplicacion === 'categorias') return 'Categorías específicas';
  return cupon.aplicacion;
}

export async function vistaCupones(contenedorRaiz) {
  if (!exigirSesionEmpresa()) return;

  const empresa = obtenerEstadoSesion().empresa ?? {};
  const tieneCupones = tieneFeature(empresa, 'cupones');
  const esPro = empresa.plan?.nombre === 'Pro';

  const contenido = montarShell(contenedorRaiz, '/cupones');
  contenido.innerHTML = `
    <div class="pf-fila pf-fila--separada" style="margin-bottom: 16px;">
      <div>
        <h1>Cupones</h1>
        <p class="pf-texto-suave" style="margin: 4px 0 0;">
          Crea descuentos para incentivar las compras de tus clientes.
        </p>
      </div>
      ${
        tieneCupones
          ? `
        <button type="button" class="pf-boton pf-boton--primario" id="pf-btn-nuevo-cupon">
          + Nuevo cupón
        </button>
      `
          : ''
      }
    </div>

    ${
      !tieneCupones
        ? `
      <div class="pf-campo__error" style="padding: 16px; margin-bottom: 16px; display: flex; align-items: flex-start; gap: 12px;">
        <span style="color: var(--pf-color-premium); flex-shrink: 0; margin-top: 2px;">${iconoCandado()}</span>
        <div>
          <strong>Cupones</strong> — función bloqueada<br>
          Disponible desde el plan <strong>Medio</strong>.<br>
          Premia a tus clientes con descuentos y promociones.<br>
          <button type="button" class="pf-boton pf-boton--premium" id="pf-btn-ver-planes" style="margin-top: 12px;">
            Mejorar plan
          </button>
        </div>
      </div>
    `
        : ''
    }

    <div id="pf-cupones-tabla"><p class="pf-texto-suave">Cargando…</p></div>

    <pf-modal titulo="Nuevo cupón" id="pf-modal-cupon"></pf-modal>
  `;

  if (!tieneCupones) {
    const btnVerPlanes = contenido.querySelector('#pf-btn-ver-planes');
    if (btnVerPlanes) {
      btnVerPlanes.addEventListener('click', () => {
        window.location.hash = '#/planes';
      });
    }
    return;
  }

  await cargarTabla(contenido, esPro);

  const modal = contenido.querySelector('#pf-modal-cupon');
  contenido.querySelector('#pf-btn-nuevo-cupon').addEventListener('click', async () => {
    await montarFormulario(modal, null, esPro, () => cargarTabla(contenido, esPro));
    modal.setAttribute('titulo', 'Nuevo cupón');
    modal.abrir();
  });
}

async function cargarTabla(contenido, esPro) {
  const destino = contenido.querySelector('#pf-cupones-tabla');
  const { data, error } = await listarCupones();

  if (error) {
    destino.innerHTML = `<p class="pf-campo__error">No se pudieron cargar los cupones.</p>`;
    return;
  }

  const modal = contenido.querySelector('#pf-modal-cupon');

  const tabla = crearTabla({
    filaVacia: 'Todavía no hay cupones. Crea el primero con el botón de arriba.',
    columnas: [
      {
        clave: 'codigo',
        titulo: 'Código',
        render: (fila) => {
          const span = document.createElement('span');
          span.textContent = fila.codigo;
          span.style.fontWeight = '600';
          span.style.fontFamily = 'monospace';
          return span;
        },
      },
      {
        titulo: 'Descuento',
        render: (fila) => {
          const valor =
            fila.tipo === 'porcentaje' ? `${fila.valor}%` : `S/ ${Number(fila.valor).toFixed(2)}`;
          const span = document.createElement('span');
          span.textContent = valor;
          span.style.fontWeight = '600';
          span.style.color = 'var(--pf-color-exito)';
          return span;
        },
      },
      {
        titulo: 'Aplicación',
        render: (fila) => textoAplicacion(fila),
      },
      {
        titulo: 'Vigencia',
        render: (fila) => {
          const inicio = formatearFecha(fila.fecha_inicio);
          const fin = formatearFecha(fila.fecha_fin);
          const span = document.createElement('span');
          span.textContent = `${inicio} → ${fin}`;
          span.style.fontSize = '0.875rem';
          return span;
        },
      },
      {
        titulo: 'Usos',
        render: (fila) => {
          const actual = Number(fila.usos_actuales ?? 0);
          const maximo = fila.usos_maximos ? `${fila.usos_maximos}` : '∞';
          const span = document.createElement('span');
          span.textContent = `${actual} / ${maximo}`;
          const porcentaje = fila.usos_maximos ? actual / Number(fila.usos_maximos) : 0;
          if (porcentaje >= 1) span.style.color = 'var(--pf-color-peligro)';
          else if (porcentaje >= 0.8) span.style.color = 'var(--pf-color-alerta)';
          return span;
        },
      },
      {
        titulo: 'Estado',
        render: (fila) => renderizarBadge(derivarEstado(fila)),
      },
      {
        titulo: 'Acciones',
        render: (fila) =>
          crearAccionesFila(fila, modal, esPro, () => cargarTabla(contenido, esPro)),
      },
    ],
    filas: data ?? [],
  });

  destino.innerHTML = '';
  destino.appendChild(tabla);
}

function crearAccionesFila(cupon, modal, esPro, alTerminar) {
  const contenedor = document.createElement('div');
  contenedor.className = 'pf-fila';

  const botonEditar = document.createElement('button');
  botonEditar.type = 'button';
  botonEditar.className = 'pf-boton pf-boton--secundario pf-boton--chico';
  botonEditar.textContent = 'Editar';
  botonEditar.addEventListener('click', async () => {
    await montarFormulario(modal, cupon.id, esPro, alTerminar);
    modal.setAttribute('titulo', `Editar: ${cupon.codigo}`);
    modal.abrir();
  });

  const botonActivar = document.createElement('button');
  botonActivar.type = 'button';
  botonActivar.className = 'pf-boton pf-boton--secundario pf-boton--chico';
  botonActivar.textContent = 'Activar';
  botonActivar.addEventListener('click', async () => {
    const { error } = await activarCupon(cupon.id);
    if (error) {
      mostrarToast(mensajeErrorAmigable(error), { tono: 'peligro' });
    } else {
      mostrarToast('Cupón activado', { tono: 'exito' });
      alTerminar();
    }
  });

  const botonDesactivar = document.createElement('button');
  botonDesactivar.type = 'button';
  botonDesactivar.className = 'pf-boton pf-boton--secundario pf-boton--chico';
  botonDesactivar.textContent = 'Desactivar';
  botonDesactivar.addEventListener('click', async () => {
    const { error } = await desactivarCupon(cupon.id);
    if (error) {
      mostrarToast(mensajeErrorAmigable(error), { tono: 'peligro' });
    } else {
      mostrarToast('Cupón desactivado', { tono: 'exito' });
      alTerminar();
    }
  });

  const botonEliminar = document.createElement('button');
  botonEliminar.type = 'button';
  botonEliminar.className = 'pf-boton pf-boton--peligro pf-boton--chico';
  botonEliminar.textContent = 'Eliminar';
  botonEliminar.addEventListener('click', async () => {
    if (!confirm('¿Eliminar este cupón?')) return;
    const { error } = await eliminarCupon(cupon.id);
    if (error) {
      mostrarToast(mensajeErrorAmigable(error), { tono: 'peligro' });
    } else {
      mostrarToast('Cupón eliminado', { tono: 'exito' });
      alTerminar();
    }
  });

  contenedor.appendChild(botonEditar);

  if (cupon.estado === 'activo') {
    contenedor.appendChild(botonDesactivar);
  } else {
    contenedor.appendChild(botonActivar);
  }

  contenedor.appendChild(botonEliminar);

  return contenedor;
}

function mostrarErrorCampo(formulario, nombre, mensaje) {
  let campo = formulario.querySelector(`[data-error="${nombre}"]`);
  if (!campo) {
    campo = document.createElement('span');
    campo.className = 'pf-campo__error';
    campo.setAttribute('data-error', nombre);
    campo.style.fontSize = '0.8rem';
    campo.style.display = 'block';
    campo.style.marginTop = '4px';
    const input = formulario.querySelector(`[name="${nombre}"]`);
    if (input) {
      input.insertAdjacentElement('afterend', campo);
    } else {
      formulario.appendChild(campo);
    }
  }
  campo.textContent = mensaje;
}

function limpiarErrores(formulario) {
  formulario.querySelectorAll('[data-error]').forEach((el) => {
    el.textContent = '';
  });
}

async function montarFormulario(modal, cuponId, esPro, alTerminar) {
  const esEdicion = Boolean(cuponId);
  let cupon = null;

  if (esEdicion) {
    const { data, error } = await listarCupones();
    if (!error && data) {
      cupon = data.find((c) => c.id === cuponId) ?? null;
    }
  }

  const formulario = document.createElement('form');
  formulario.innerHTML = `
    <div class="pf-formulario-cupon">
      <div class="pf-seccion-formulario">
        <p class="pf-seccion-formulario__titulo">INFORMACIÓN DEL CUPÓN</p>

        <div class="pf-campo">
          <label for="pf-codigo-cupon">Código del cupón</label>
          <input type="text" name="codigo" id="pf-codigo-cupon" value="${escapeAttr(cupon?.codigo ?? '')}" required autocomplete="off" placeholder="Ej: CUPON10" />
          <small>Sin espacios, en mayúsculas. Se generará automáticamente si lo dejas vacío.</small>
        </div>

        <div class="pf-campo pf-campo--par">
          <div class="pf-campo">
            <label for="pf-tipo-cupon">Tipo de descuento</label>
            <select name="tipo" id="pf-tipo-cupon">
              <option value="porcentaje" ${cupon?.tipo === 'porcentaje' ? 'selected' : ''}>Porcentaje</option>
              <option value="fijo" ${cupon?.tipo === 'fijo' ? 'selected' : ''}>Fijo</option>
            </select>
          </div>
          <div class="pf-campo">
            <label for="pf-valor-cupon">Valor</label>
            <input type="number" name="valor" id="pf-valor-cupon" value="${cupon?.valor ?? ''}" step="0.01" min="0" required placeholder="Ej: 10" />
            <small>Para porcentaje: número entre 0 y 100.</small>
          </div>
        </div>
      </div>

      <div class="pf-seccion-formulario">
        <p class="pf-seccion-formulario__titulo">CONDICIONES</p>

        <div class="pf-campo pf-campo--par">
          <div class="pf-campo">
            <label for="pf-compra-minima">Compra mínima <span class="pf-campo--opcional">(opcional)</span></label>
            <input type="number" name="compra_minima" id="pf-compra-minima" value="${cupon?.compra_minima ?? ''}" step="0.01" min="0" placeholder="Ej: 100" />
            <small>Déjalo vacío para permitir cualquier compra.</small>
          </div>
          <div class="pf-campo">
            <label for="pf-aplicacion">Aplicar a</label>
            <select name="aplicacion" id="pf-aplicacion">
              <option value="todos" ${cupon?.aplicacion === 'todos' ? 'selected' : ''}>Todos los productos</option>
              <option value="productos" ${cupon?.aplicacion === 'productos' ? 'selected' : ''}>Productos específicos</option>
              <option value="categorias" ${cupon?.aplicacion === 'categorias' ? 'selected' : ''}>Categorías específicas</option>
            </select>
            <small>Elige si el descuento aplica a toda la tienda o solo a productos/categorías seleccionados.</small>
          </div>
        </div>
      </div>

      <div class="pf-seccion-formulario">
        <p class="pf-seccion-formulario__titulo">VIGENCIA</p>

        <div class="pf-campo pf-campo--par">
          <div class="pf-campo">
            <label for="pf-fecha-inicio">Fecha de inicio <span class="pf-campo--opcional">(opcional)</span></label>
            <input type="datetime-local" name="fecha_inicio" id="pf-fecha-inicio" value="${cupon?.fecha_inicio ? cupon.fecha_inicio.slice(0, 16) : ''}" />
            <small>Déjalo vacío para activar inmediatamente.</small>
          </div>
          <div class="pf-campo">
            <label for="pf-fecha-fin">Fecha de vencimiento <span class="pf-campo--opcional">(opcional)</span></label>
            <input type="datetime-local" name="fecha_fin" id="pf-fecha-fin" value="${cupon?.fecha_fin ? cupon.fecha_fin.slice(0, 16) : ''}" />
            <small>Déjalo vacío para que no expire.</small>
          </div>
        </div>
      </div>

      <div class="pf-seccion-formulario">
        <p class="pf-seccion-formulario__titulo">LÍMITES</p>

        <div class="pf-campo pf-campo--par">
          <div class="pf-campo">
            <label for="pf-usos-maximos">Usos máximos <span class="pf-campo--opcional">(opcional)</span></label>
            <input type="number" name="usos_maximos" id="pf-usos-maximos" value="${cupon?.usos_maximos ?? ''}" min="1" placeholder="Ej: 100" />
            <small>Déjalo vacío para usos ilimitados.</small>
          </div>
          <div class="pf-campo">
            <label for="pf-limite-cliente">Límite por cliente</label>
            <input type="number" name="limite_por_cliente" id="pf-limite-cliente" value="${cupon?.limite_por_cliente ?? 1}" min="1" />
            <small>Cuántas veces puede usar este cupón un mismo cliente (por teléfono).</small>
          </div>
        </div>
      </div>

      ${
        esPro
          ? `
      <div class="pf-seccion-formulario">
        <p class="pf-seccion-formulario__titulo">OPCIONES PRO</p>
        <div class="pf-campo">
          <label class="pf-campo-checkbox">
            <input type="checkbox" name="combinable" value="true" ${cupon?.combinable ? 'checked' : ''} />
            <span>Cupón combinable</span>
          </label>
          <small>Los clientes podrán usar este cupón junto con otros descuentos.</small>
        </div>
      </div>
      `
          : ''
      }
    </div>

    <div class="pf-formulario__footer">
      <button type="button" class="pf-boton pf-boton--secundario" id="pf-cancelar-cupon">Cancelar</button>
      <button type="submit" class="pf-boton pf-boton--primario">${esEdicion ? 'Guardar cambios' : 'Crear cupón'}</button>
    </div>
  `;

  modal.innerHTML = '';
  modal.appendChild(formulario);

  formulario.querySelector('#pf-cancelar-cupon')?.addEventListener('click', () => {
    modal.cerrar();
  });

  formulario.addEventListener('submit', async (evento) => {
    evento.preventDefault();
    limpiarErrores(formulario);

    const datos = new FormData(formulario);
    const codigo = String(datos.get('codigo') ?? '')
      .trim()
      .toUpperCase();
    const tipo = String(datos.get('tipo') ?? 'porcentaje');
    const valor = Number(datos.get('valor'));
    const compraMinima = datos.get('compra_minima') ? Number(datos.get('compra_minima')) : null;
    const fechaInicio = datos.get('fecha_inicio')
      ? new Date(datos.get('fecha_inicio')).toISOString()
      : null;
    const fechaFin = datos.get('fecha_fin') ? new Date(datos.get('fecha_fin')).toISOString() : null;
    const usosMaximos = datos.get('usos_maximos') ? Number(datos.get('usos_maximos')) : null;
    const limitePorCliente = Number(datos.get('limite_por_cliente')) || 1;
    const aplicacion = String(datos.get('aplicacion') ?? 'todos');
    const combinable = esPro
      ? (formulario.querySelector('[name="combinable"]')?.checked ?? false)
      : false;

    let tieneError = false;

    if (!codigo) {
      mostrarErrorCampo(formulario, 'codigo', 'El código es obligatorio.');
      tieneError = true;
    }

    if (!tipo || isNaN(valor) || valor <= 0) {
      mostrarErrorCampo(formulario, 'valor', 'El descuento debe ser mayor que 0.');
      tieneError = true;
    }

    if (tipo === 'porcentaje' && valor > 100) {
      mostrarErrorCampo(formulario, 'valor', 'El descuento no puede superar el 100%.');
      tieneError = true;
    }

    if (compraMinima !== null && compraMinima < 0) {
      mostrarErrorCampo(formulario, 'compra_minima', 'La compra mínima no puede ser negativa.');
      tieneError = true;
    }

    if (fechaInicio && fechaFin && new Date(fechaInicio) > new Date(fechaFin)) {
      mostrarErrorCampo(
        formulario,
        'fecha_fin',
        'La fecha de vencimiento debe ser posterior a la fecha de inicio.',
      );
      tieneError = true;
    }

    if (aplicacion !== 'todos') {
      const select = formulario.querySelector('#pf-aplicacion');
      const ayuda = select?.nextElementSibling;
      if (ayuda && ayuda.tagName === 'SMALL') {
        ayuda.textContent =
          'Recuerda seleccionar los productos o categorías desde la edición del cupón.';
        ayuda.style.color = 'var(--pf-color-alerta)';
      }
    }

    if (tieneError) {
      const primerError = formulario.querySelector('[data-error]:not(:empty)');
      if (primerError) primerError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const payload = {
      codigo,
      tipo,
      valor,
      compra_minima: compraMinima,
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
      usos_maximos: usosMaximos,
      limite_por_cliente: limitePorCliente,
      aplicacion,
      combinable,
    };

    let error;
    if (esEdicion) {
      const resultado = await actualizarCupon(cuponId, payload);
      error = resultado.error;
    } else {
      const resultado = await crearCupon(payload);
      error = resultado.error;
    }

    if (error) {
      mostrarToast(mensajeErrorAmigable(error), { tono: 'peligro' });
    } else {
      mostrarToast(esEdicion ? 'Cupón actualizado' : 'Cupón creado', { tono: 'exito' });
      modal.cerrar();
      alTerminar();
    }
  });
}
